// src/components/ReservationRow.jsx (inline edit + delete)

import React, { useMemo, useState } from 'react';
import formatDate from '../formatDate';

function isTodayInRange(startDate, endDate) {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const start = new Date(startDate);
	start.setHours(0, 0, 0, 0);

	const end = new Date(endDate);
	end.setHours(0, 0, 0, 0);

	return today >= start && today <= end; // [start, end] — end date is included
}

function yyyyMmDd(value) {
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '';
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

export default function ReservationRow({
	reservation,
	isEditMode,
	onUpdate,
	onDelete,
}) {
	const id = reservation.id || reservation._id;
	const active = useMemo(
		() => isTodayInRange(reservation.startDate, reservation.endDate),
		[reservation.startDate, reservation.endDate]
	);

	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [err, setErr] = useState('');

	const [name, setName] = useState(reservation.name || '');
	const [startDate, setStartDate] = useState(yyyyMmDd(reservation.startDate));
	const [endDate, setEndDate] = useState(yyyyMmDd(reservation.endDate));

	const cardClass = active
		? 'rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3'
		: 'rounded-xl border border-slate-200 bg-white px-4 py-3';

	const reset = () => {
		setName(reservation.name || '');
		setStartDate(yyyyMmDd(reservation.startDate));
		setEndDate(yyyyMmDd(reservation.endDate));
		setErr('');
	};

	const save = async () => {
		setErr('');
		const trimmed = name.trim();
		if (!trimmed) return setErr('Name required');
		if (!startDate) return setErr('Start required');
		if (!endDate) return setErr('End required');

		const s = new Date(startDate);
		const e = new Date(endDate);
		if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
			return setErr('Invalid date');
		if (e < s) return setErr('End must be on or after start');

		try {
			setSaving(true);
			await onUpdate(id, {
				name: trimmed,
				startDate: s.toISOString(),
				endDate: e.toISOString(),
			});
			setEditing(false);
		} catch (e2) {
			setErr(e2?.response?.data?.message || e2?.message || 'Update failed');
		} finally {
			setSaving(false);
		}
	};

	const remove = async () => {
		setErr('');
		if (!window.confirm('Delete this reservation?')) return;
		try {
			setSaving(true);
			await onDelete(id);
		} catch (e2) {
			setErr(e2?.response?.data?.message || e2?.message || 'Delete failed');
			setSaving(false);
		}
	};

	return (
		<div className={cardClass}>
			<div className='flex items-start justify-between gap-3'>
				<div className='min-w-0 w-full'>
					{!editing ? (
						<>
							<div className='truncate text-sm font-semibold text-slate-900'>
								{reservation.name}
							</div>
							<div className='mt-1 text-xs text-slate-600'>
								{formatDate(reservation.startDate)} →{' '}
								{formatDate(reservation.endDate)}
							</div>
						</>
					) : (
						<div className='grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1'>
							<input
								className='rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-300'
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder='Name'
								disabled={saving}
							/>
							<input
								type='date'
								className='rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-300'
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								disabled={saving}
							/>
							<input
								type='date'
								className='rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-300'
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								disabled={saving}
							/>
						</div>
					)}

					{err ? (
						<div className='mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2'>
							{err}
						</div>
					) : null}
				</div>

				<div className='shrink-0 flex flex-col items-end gap-2'>
					<div
						className={
							active
								? 'rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200 px-2.5 py-1 text-[11px]'
								: 'rounded-full bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-1 text-[11px]'
						}
					>
						{active
							? 'Active'
							: Date.now() < new Date(reservation.startDate)
							? 'Upcoming'
							: 'Past'}
					</div>

					{isEditMode && reservation.startDate > new Date() ? (
						<div className='flex items-center gap-2'>
							{!editing ? (
								<>
									<button
										type='button'
										onClick={() => {
											reset();
											setEditing(true);
										}}
										className='rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60'
										disabled={saving}
									>
										Edit
									</button>
									<button
										type='button'
										onClick={remove}
										className='rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60'
										disabled={saving}
									>
										Delete
									</button>
								</>
							) : (
								<>
									<button
										type='button'
										onClick={save}
										className='rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-60'
										disabled={saving}
									>
										{saving ? 'Saving…' : 'Save'}
									</button>
									<button
										type='button'
										onClick={() => {
											reset();
											setEditing(false);
										}}
										className='rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60'
										disabled={saving}
									>
										Cancel
									</button>
								</>
							)}
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
}
